import { alpha, createTheme } from '@mui/material/styles';

const fontFamily = `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;

const getSurfaceStyles = (mode) => {
    if (mode === 'dark') {
        return {
            backgroundColor: 'rgba(17, 25, 40, 0.72)',
            border: '1px solid rgba(148, 163, 184, 0.22)',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.28)',
        };
    }
    return {
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        border: '1px solid rgba(209, 213, 219, 0.32)',
        boxShadow: '0 12px 30px rgba(15, 23, 42, 0.1)',
    };
};

const getBackdropStyle = (mode) => ({
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderRadius: 14,
    ...getSurfaceStyles(mode),
});

const lightGradient = 'linear-gradient(120deg, rgba(255,255,255,0.92) 0%, rgba(224,232,255,0.82) 50%, rgba(235,240,255,0.88) 100%)';
const darkGradient = 'linear-gradient(140deg, rgba(12,18,28,0.96) 0%, rgba(20,30,48,0.92) 45%, rgba(30,41,59,0.94) 100%)';

const baseThemeOptions = (mode) => ({
    palette: {
        mode,
        ...(mode === 'light'
            ? {
                  primary: { main: '#2563eb' },
                  secondary: { main: '#8b5cf6' },
                  background: {
                      default: alpha('#f8fafc', 0.65),
                      paper: alpha('#ffffff', 0.65),
                  },
                  text: {
                      primary: '#0f172a',
                      secondary: alpha('#0f172a', 0.7),
                  },
                  divider: alpha('#0f172a', 0.12),
              }
            : {
                  primary: { main: '#60a5fa' },
                  secondary: { main: '#a855f7' },
                  background: {
                      default: alpha('#0f172a', 0.75),
                      paper: alpha('#111827', 0.62),
                  },
                  text: {
                      primary: '#f8fafc',
                      secondary: alpha('#f8fafc', 0.72),
                  },
                  divider: alpha('#f8fafc', 0.14),
              }),
    },
    shape: { borderRadius: 14 },
    typography: {
        fontFamily,
        h1: { fontWeight: 600 },
        h2: { fontWeight: 600 },
        h3: { fontWeight: 600 },
        h4: { fontWeight: 600 },
        h5: { fontWeight: 600 },
        h6: { fontWeight: 600 },
        subtitle1: { fontWeight: 500 },
        button: { fontWeight: 600, textTransform: 'none' },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                ':root': { colorScheme: mode },
                body: {
                    minHeight: '100vh',
                    backgroundImage: mode === 'dark' ? darkGradient : lightGradient,
                    backgroundAttachment: 'fixed',
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                },
                '*::selection': {
                    background: mode === 'dark' ? alpha('#60a5fa', 0.35) : alpha('#2563eb', 0.35),
                },
            },
        },
        MuiPaper: {
            defaultProps: {
                elevation: 0,
                square: false,
            },
            styleOverrides: {
                root: {
                    transition: 'background-color 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
                    ...getBackdropStyle(mode),
                },
            },
        },
        MuiAppBar: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: {
                    ...getBackdropStyle(mode),
                    backgroundImage: 'none',
                    position: 'sticky',
                    top: 0,
                    boxShadow: 'none',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    ...getBackdropStyle(mode),
                    borderRight: mode === 'dark'
                        ? '1px solid rgba(148,163,184,0.28)'
                        : '1px solid rgba(209,213,219,0.28)',
                    backgroundImage: 'none',
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: { ...getBackdropStyle(mode) },
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: { ...getBackdropStyle(mode) },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    ...getBackdropStyle(mode),
                    padding: 16,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: { ...getBackdropStyle(mode) },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 14,
                    paddingInline: 18,
                    paddingBlock: 8,
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
                },
                contained: {
                    boxShadow: '0 8px 24px rgba(37, 99, 235, 0.3)',
                },
                outlined: {
                    borderColor: alpha('#2563eb', mode === 'dark' ? 0.35 : 0.25),
                    backgroundColor: alpha('#2563eb', 0.05),
                },
                text: {
                    color: mode === 'dark' ? '#f8fafc' : '#1e293b',
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: 12,
                    lineHeight: 1.5,
                    maxWidth: 280,
                    padding: '10px 12px',
                    borderRadius: 10,
                    color: '#f8fafc',
                    backgroundColor: mode === 'dark'
                        ? alpha('#020617', 0.96)
                        : alpha('#0f172a', 0.96),
                    border: mode === 'dark'
                        ? '1px solid rgba(148, 163, 184, 0.24)'
                        : '1px solid rgba(148, 163, 184, 0.18)',
                    boxShadow: '0 16px 36px rgba(15, 23, 42, 0.24)',
                    backdropFilter: 'none',
                    WebkitBackdropFilter: 'none',
                },
                arrow: {
                    color: mode === 'dark'
                        ? alpha('#020617', 0.96)
                        : alpha('#0f172a', 0.96),
                },
            },
        },
    },
});

export const createGlassTheme = (mode = 'light') => createTheme(baseThemeOptions(mode));
